import { groundScreenY } from '../game/ground';

// Reflective ground — where the alley's street is wet enough to hold a
// reflection, and how strongly. Queried per actor per frame; the answer drives
// the alpha and the mirror plane of that actor's reflected rig.
//
// This is a SCENE fact, not an actor fact: whether you cast a reflection is a
// property of the ground you stand on, so the zones live with the light skin and
// the puddle rather than on the Actor.
//
// On the belt, a zone is a RECTANGLE of ground — an x-interval along the street
// crossed with a z-interval into it. It used to be a bare x-interval, because the
// platformer's ground was a line and the actor touched it at one point. Now the
// ground is a plane and the actor stands somewhere on it, so the puddle has a
// near edge and a far edge as well as two sides.
//
// The mirror plane is NOT a property of the zone. It is `groundScreenY(z)` — the
// screen height of the ground under the actor's own feet — so an actor standing
// at the back of the street reflects into the back of the street, not into the
// kerb. Zones only say how wet it is there.

export interface ReflectiveZone {
  /** Along-street extent, world px. */
  x0: number;
  x1: number;
  /** Into-street extent, world px along the oblique axis. */
  z0: number;
  z1: number;
  /** Reflection opacity at the zone's core, 0..1. */
  strength: number;
  /** Edge softness, world px, applied on all four sides. */
  feather: number;
  /** How high above the ground an actor still reflects, world px of `h`. */
  airGap: number;
}

export interface GroundReflection {
  /** 0 = dry ground (or too high above it): draw nothing. */
  strength: number;
  /** Screen y of the water's surface under the actor. */
  planeY: number;
}

/** How reflective the ground is under an actor standing at (x, z), `h` above it.
 *  Zones overlap freely; the strongest wins. */
export function reflectionAt(
  zones: readonly ReflectiveZone[],
  x: number,
  z: number,
  h: number,
): GroundReflection {
  let strength = 0;
  for (const zone of zones) {
    const s = zone.strength * inset(zone, x, z) * vertical(zone, h);
    if (s > strength) strength = s;
  }
  return { strength, planeY: groundScreenY(z) };
}

/** 1 across the zone's core, smoothly to 0 across `feather` at every edge. The
 *  softness matters: without it a reflection pops into existence the instant a
 *  foot crosses a boundary, which reads as a bug rather than as water. */
function inset(zone: ReflectiveZone, x: number, z: number): number {
  const inX = Math.min(x - zone.x0, zone.x1 - x);
  const inZ = Math.min(z - zone.z0, zone.z1 - z);
  const inside = Math.min(inX, inZ);
  if (inside <= 0) return 0;
  return zone.feather > 0 ? smoothstep(Math.min(1, inside / zone.feather)) : 1;
}

/** 1 standing on the ground, fading to 0 at `airGap` above it. This gate is what
 *  dissolves the reflection as an actor jumps, and it is measured on `h` — the
 *  actor's true height above the plane — so it no longer has to be inferred from
 *  a screen y that now also encodes depth. */
function vertical(zone: ReflectiveZone, h: number): number {
  if (h <= 0) return 1;
  if (h >= zone.airGap) return 0;
  return smoothstep(1 - h / zone.airGap);
}

const smoothstep = (t: number) => t * t * (3 - 2 * t);
